import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Badge,
  Spinner,
  Modal,
  TextInput,
  Label,
  Select,
} from "flowbite-react";
import { Store } from "../context/store";
import apiRequest from "../utils/apiRequest";
import CustomToast from "../components/Toast";
import PaymentReviewModal from "../components/CustomerPaymentReview";
import { useAllPermissions } from "../utils/usePermission";
import DashboardLayout from "../components/layout/DashboardLayout";

export default function CommissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const store = useContext(Store);
  const [baseUrl] = store.url;

  const [commission, setCommission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Customer payment modal state
  const [showCustomerPaymentModal, setShowCustomerPaymentModal] =
    useState(false);
  const [customerAmountPaid, setCustomerAmountPaid] = useState("");
  const [customerDatePaid, setCustomerDatePaid] = useState("");
  const [paymentProof, setPaymentProof] = useState(null);
  const [customerPaymentError, setCustomerPaymentError] = useState("");

  // Marketer payment modal state
  const [showMarketerPaymentModal, setShowMarketerPaymentModal] =
    useState(false);
  const [marketerAmountPaid, setMarketerAmountPaid] = useState("");
  const [marketerDatePaid, setMarketerDatePaid] = useState("");
  const [marketerPaymentError, setMarketerPaymentError] = useState("");
  const [totalCommissionExpected, setTotalCommissionExpected] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [selectedStatus, setSelectedStatus] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const { canWriteCommission } = useAllPermissions();

  const onStatusChange = (e) => {
    const value = e.target.value;
    if (value === "" || value === commission.status) return;

    setSelectedStatus(value);
    setOpenModal(true); // open confirm modal
  };

  const confirmAction = async () => {
    setErrorMessage("");

    if (selectedStatus === "approved") {
      if (!totalCommissionExpected || Number(totalCommissionExpected) <= 0) {
        setErrorMessage("Total commission expected is required for approval.");
        return;
      }
    }

    await handleCommissionApproval(selectedStatus, totalCommissionExpected);
    setOpenModal(false);
    setTotalCommissionExpected("");
  };

  const fetchCommission = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/commission/${id}/admin`;
      const { data } = await apiRequest(url, "GET", null, token);
      setCommission(data);
    } catch (error) {
      alert("Failed to load commission request");
      navigate("/commissions");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, id, navigate]);

  useEffect(() => {
    fetchCommission();
  }, [fetchCommission]);

  // Add customer payment
  const addCustomerPayment = async () => {
    if (
      !customerAmountPaid ||
      isNaN(customerAmountPaid) ||
      Number(customerAmountPaid) <= 0
    ) {
      setCustomerPaymentError("Please enter a valid amount");
      return;
    }

    setCustomerPaymentError("");
    setUpdating(true);
    try {
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/customer-payment-commission/${id}`;

      const formData = new FormData();
      formData.append("amountPaid", Number(customerAmountPaid));
      if (customerDatePaid) {
        formData.append("datePaid", customerDatePaid);
      }
      if (paymentProof) {
        formData.append("files", paymentProof);
      }

      await apiRequest(url, "PUT", formData, token);
      setShowCustomerPaymentModal(false);
      setCustomerAmountPaid("");
      setCustomerDatePaid("");
      setPaymentProof(null);
      await fetchCommission();
    } catch (err) {
      alert(err.message || "Failed to add payment");
    } finally {
      setUpdating(false);
    }
  };

  const addMarketerPayment = async () => {
    if (
      !marketerAmountPaid ||
      isNaN(marketerAmountPaid) ||
      Number(marketerAmountPaid) <= 0
    ) {
      setMarketerPaymentError("Please enter a valid amount");
      return;
    }

    if (Number(marketerAmountPaid) > outstanding) {
      setMarketerPaymentError("Amount entered is greater than Outstanding.");
      return;
    }

    setMarketerPaymentError("");
    setUpdating(true);
    try {
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/request/commission/${id}`;

      await apiRequest(
        url,
        "PUT",
        {
          amountPaid: Number(marketerAmountPaid),
          datePaid: marketerDatePaid ? new Date(marketerDatePaid) : new Date(),
        },
        token,
      );

      setShowMarketerPaymentModal(false);
      setMarketerAmountPaid("");
      setMarketerDatePaid("");
      await fetchCommission();
    } catch (err) {
      alert(err.message || "Failed to add marketer payment");
    } finally {
      setUpdating(false);
    }
  };

  const handleCommissionApproval = async (status, commissionValue) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/commission/${id}`;

      const payload = { status };

      if (status === "approved") {
        payload.totalCommissionExpected = Number(commissionValue);
      }

      await apiRequest(url, "PUT", payload, token);
      await fetchCommission();
    } catch (err) {
      alert(err.message || "Failed to approve or reject request");
    } finally {
      setUpdating(false);
    }
  };

  const handleProcessPayment = async (
    paymentId,
    commissionId,
    status,
    amountPaid,
  ) => {
    try {
      if (status === "paid" && !amountPaid) {
        setToastOpen(true);
        setToastMessage("Amount paid is required!");
        setToastType("error");
        return;
      }

      const payload = {
        status,
        ...(status === "paid" && { amountPaid: Number(amountPaid) }),
      };

      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/process/${paymentId}/commission/${commissionId}`;

      await apiRequest(url, "PUT", payload, token);
      setToastOpen(true);
      setToastMessage("Successful");
      setToastType("success");
      await fetchCommission();
    } catch (error) {
      setToastOpen(true);
      setToastType("error");
      setToastMessage(error.message || "Something went wrong");
    }
  };

  const handleLocalStatusChange = (index, status, amountPaid = null) => {
    const updated = [...commission.marketerPayments];
    updated[index].status = status;

    if (status !== "paid") {
      updated[index].amountPaid = null;
    } else if (amountPaid !== null) {
      updated[index].amountPaid = amountPaid;
    }

    setCommission({
      ...commission,
      marketerPayments: updated,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!commission) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        Commission request not found
      </div>
    );
  }

  const outstanding =
    commission.commissionEarnedSoFar - commission.commissionPaidSoFar || 0;

  return (
    <DashboardLayout active="commissions">
        <div className="min-h-screen">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header & Back Button */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <Button size="xs" color="gray" onClick={() => navigate(-1)}>
                  &larr; Back to Commissions
                </Button>
                {/* <h1 className="text-3xl font-extrabold text-gray-900">
                  Commission ID: **{id.substring(0, 8)}...**
                </h1> */}
              </div>
            </div>
            {/* Status & Action Bar */}
            <Card className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center shadow-lg border-l-4 border-blue-500">
              <div className="flex items-center space-x-4 mb-3 md:mb-0">
                <span className="text-xl font-semibold">Overall Status:</span>
                <Badge
                  color={
                    commission.status === "approved"
                      ? "success"
                      : commission.status === "pending"
                        ? "warning"
                        : "failure"
                  }
                  size="lg"
                  className="capitalize text-lg"
                >
                  {commission.status}
                </Badge>
              </div>

              {commission.status === "pending" && (
                <div className="w-full md:w-64">
                  <Select
                    disabled={!canWriteCommission}
                    value=""
                    onChange={onStatusChange}
                    className="focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Approve or Reject Request</option>
                    <option value="approved">✅ Approve</option>
                    <option value="rejected">❌ Reject</option>
                  </Select>
                </div>
              )}
            </Card>
            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column: Core Details (2/3 width) */}
              <div className="lg:col-span-2 space-y-6">
                {/* Marketer Info */}
                <Card className="shadow">
                  <h2 className="text-2xl font-bold mb-3 text-gray-700">
                    🧑 Marketer & Property Details
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-600">
                    <InfoBox title="Marketer Name">
                      {commission.marketerId
                        ? `${commission.marketerId.firstName} ${commission.marketerId.lastName}`
                        : "N/A"}
                    </InfoBox>
                    <InfoBox title="Marketer Phone">
                      {commission.marketerId?.phone || "N/A"}
                    </InfoBox>
                    <InfoBox title="Property Sold">
                      <strong>{commission.propertySold}</strong>
                    </InfoBox>
                    <InfoBox title="Property Sold Unit">
                      <strong>{commission.unitDetails.unit || "N/A"}</strong>
                    </InfoBox>
                    <InfoBox title="Location">{commission.location}</InfoBox>
                    <InfoBox title="Date of Sale">
                      {new Date(commission.dateOfSales).toLocaleDateString(
                        "en-GB",
                        { day: "numeric", month: "short", year: "numeric" },
                      )}
                    </InfoBox>
                  </div>
                </Card>
                {/* Financial Summary */}
                <Card className="shadow">
                  <h2 className="text-2xl font-bold mb-4 text-gray-700">
                    💰 Financial Summary
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-xl font-semibold">
                    <FinancialStat
                      title="Property Price"
                      value={commission.propertyPrice.toLocaleString("en-US", {
                        style: "currency",
                        currency: "NGN",
                      })}
                      color="text-blue-600"
                    />
                    <FinancialStat
                      title="Total Commission Expected"
                      value={commission.totalCommissionExpected.toLocaleString(
                        "en-US",
                        {
                          style: "currency",
                          currency: "NGN",
                        },
                      )}
                      color="text-green-600"
                    />
                    <FinancialStat
                      title="Commission Paid So Far"
                      value={commission.commissionPaidSoFar.toLocaleString(
                        "en-US",
                        {
                          style: "currency",
                          currency: "NGN",
                        },
                      )}
                      color="text-yellow-600"
                    />
                    <FinancialStat
                      title="Commission Earned So Far"
                      value={commission.commissionEarnedSoFar.toLocaleString(
                        "en-US",
                        {
                          style: "currency",
                          currency: "NGN",
                        },
                      )}
                      color="text-purple-600"
                    />
                    <FinancialStat
                      title="Commission Outstanding"
                      value={(
                        commission.commissionEarnedSoFar -
                        commission.commissionPaidSoFar
                      ).toLocaleString("en-US", {
                        style: "currency",
                        currency: "NGN",
                      })}
                      color="text-red-600"
                    />
                  </div>
                </Card>
                {/* Remarks */}
                {commission.remarks && (
                  <Card className="shadow">
                    <h3 className="text-xl font-semibold mb-2 text-gray-700">
                      📝 Marketer Remarks
                    </h3>
                    <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700 bg-gray-50 p-3 rounded-md">
                      {commission.remarks}
                    </blockquote>
                  </Card>
                )}
              </div>
              {/* Right Column: Payments & Actions (1/3 width) */}
              <div className="lg:col-span-1 space-y-6">
                {/* Customer Payments */}
                <Card className="shadow">
                  <div className="flex justify-between items-center border-b pb-2 mb-3">
                    <h3 className="text-xl font-bold text-gray-700">
                      💵 Customer Payments
                    </h3>
                    <Button
                      size="sm"
                      onClick={() => setShowCustomerPaymentModal(true)}
                      disabled={
                        commission.status !== "approved" || !canWriteCommission
                      }
                    >
                      Add Customer Payment
                    </Button>
                  </div>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {commission.customerPayments.length === 0 ? (
                      <p className="text-gray-500">
                        No customer payments recorded yet.
                      </p>
                    ) : (
                      // <ul className="list-none space-y-2">
                      //   {commission.customerPayments.map((payment, i) => (
                      //     <li
                      //       key={i}
                      //       className="border-b last:border-b-0 pb-2 text-sm"
                      //     >
                      //       <p>
                      //         Amount Paid:{" "}
                      //         <strong>
                      //           {payment.amountPaid.toLocaleString("en-US", {
                      //             style: "currency",
                      //             currency: "NGN",
                      //           })}
                      //         </strong>
                      //       </p>
                      //       <p className="text-xs text-gray-500">
                      //         Paid on:{" "}
                      //         {new Date(payment.datePaid).toLocaleDateString(
                      //           "en-GB",
                      //           {
                      //             day: "numeric",
                      //             month: "short",
                      //             year: "numeric",
                      //           }
                      //         )}
                      //       </p>
                      //     </li>
                      //   ))}
                      // </ul>
                      <>
                        <ul className="list-none space-y-3">
                          {commission.customerPayments.map((payment, i) => (
                            <li
                              key={i}
                              className="p-3 border rounded-lg bg-white dark:bg-gray-800 flex justify-between items-start gap-4"
                            >
                              {/* --- Left Block: Amount & Date --- */}
                              <div className="flex flex-col">
                                {/* Amount Paid */}
                                <p className="font-semibold text-sm text-gray-700 dark:text-gray-200">
                                  Amount Paid
                                </p>
                                <div className="font-extrabold text-lg text-green-600 dark:text-green-400">
                                  {payment.amountPaid.toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "NGN",
                                  })}
                                </div>

                                {/* Date Paid */}
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Paid on:{" "}
                                  {new Date(
                                    payment.datePaid,
                                  ).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </p>
                              </div>

                              {/* --- Right Block: Status & Action Button --- */}
                              <div className="flex flex-col items-end space-y-2">
                                {/* Status Badge */}
                                <span
                                  className={`px-3 py-1 text-xs font-bold rounded-full capitalize 
                                          ${
                                            payment.paymentStatus ===
                                            "confirmed"
                                              ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300"
                                              : payment.paymentStatus ===
                                                  "rejected"
                                                ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300"
                                                : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 animate-pulse" // Pending
                                          }
                                        `}
                                >
                                  {payment.paymentStatus || "pending"}{" "}
                                  {/* Default to pending if status is missing */}
                                </span>

                                {/* Action Button (Only show if payment is pending) */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-primary hover:bg-primary/10"
                                  onClick={() => {
                                    // Set the payment data and open the modal
                                    setSelectedPayment(payment);
                                    setIsReviewModalOpen(true);
                                  }}
                                  disabled={
                                    payment.paymentStatus !== "pending" ||
                                    !canWriteCommission
                                  }
                                >
                                  Review Payment
                                </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                        {/* Trigger the modal component if open */}
                        {isReviewModalOpen && selectedPayment && (
                          <PaymentReviewModal
                            payment={selectedPayment}
                            commissionId={id}
                            onClose={() => setIsReviewModalOpen(false)}
                            onRequestProcessed={fetchCommission}
                          />
                        )}
                      </>
                    )}
                  </div>
                </Card>
                {/* Marketer Payments (Interactive) */}
                <Card className="shadow">
                  <div className="flex justify-between items-center border-b pb-2 mb-3">
                    <h3 className="text-xl font-bold text-gray-700">
                      💳 Marketer Payments
                    </h3>
                    <Button
                      size="sm"
                      onClick={() => setShowMarketerPaymentModal(true)}
                      disabled={
                        commission.status !== "approved" || !canWriteCommission
                      }
                    >
                      Add Marketer Payment
                    </Button>
                  </div>
                  <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                    {commission.marketerPayments.length === 0 ? (
                      <p className="text-gray-500">No marketer payments yet.</p>
                    ) : (
                      [...commission.marketerPayments]
                        .reverse()
                        .map((payment, i) => (
                          <div
                            key={i}
                            className="border rounded-lg p-3 bg-white shadow-sm hover:shadow transition space-y-2"
                          >
                            <p className="text-lg font-bold text-blue-600">
                              Amount:{" "}
                              {payment.amountPaid.toLocaleString("en-US", {
                                style: "currency",
                                currency: "NGN",
                              })}
                            </p>
                            <p>
                              <span className="font-semibold">Date Paid: </span>
                              {payment.datePaid
                                ? new Date(payment.datePaid).toLocaleDateString(
                                    "en-GB",
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    },
                                  )
                                : "-"}
                            </p>

                            <p className="flex items-center gap-2">
                              <span className="font-semibold text-sm">
                                Status:
                              </span>
                              <Badge
                                color={
                                  payment.paymentStatus === "paid"
                                    ? "success"
                                    : payment.paymentStatus === "pending"
                                      ? "warning"
                                      : payment.paymentStatus === "approved"
                                        ? "info"
                                        : "failure"
                                }
                                className="capitalize"
                              >
                                {payment.paymentStatus}
                              </Badge>
                            </p>

                            {/* Account Details & Actions */}
                            {payment.paymentStatus !== "paid" && (
                              <div className="mt-3 pt-2 border-t border-dashed">
                                {/* UI Buttons */}
                                {payment.paymentStatus === "pending" && (
                                  <div className="flex gap-3 mt-4">
                                    <Button
                                      size="xs"
                                      color={
                                        payment.status === "paid"
                                          ? "success"
                                          : "gray"
                                      }
                                      onClick={() =>
                                        handleLocalStatusChange(i, "paid")
                                      }
                                      disabled={!canWriteCommission}
                                    >
                                      Mark as Paid
                                    </Button>

                                    <Button
                                      size="xs"
                                      color={
                                        payment.status === "rejected"
                                          ? "failure"
                                          : "gray"
                                      }
                                      onClick={() =>
                                        handleLocalStatusChange(i, "rejected")
                                      }
                                      disabled={!canWriteCommission}
                                    >
                                      Reject
                                    </Button>
                                  </div>
                                )}

                                {/* AMOUNT PAID INPUT & PROCESS BUTTON */}
                                {payment.status === "paid" && (
                                  <div className="mt-3 space-y-2">
                                    <Label
                                      htmlFor={`amountPaid-${i}`}
                                      value="Amount Paid"
                                      className="font-semibold text-sm"
                                    />
                                    <TextInput
                                      id={`amountPaid-${i}`}
                                      type="number"
                                      step="0.01"
                                      value={payment.amountPaid || ""}
                                      onChange={(e) =>
                                        handleLocalStatusChange(
                                          i,
                                          "paid",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="Enter amount paid"
                                      max={+payment.requestedAmount}
                                      className="text-sm"
                                    />

                                    <Button
                                      size="sm"
                                      className="w-full mt-2"
                                      onClick={() =>
                                        handleProcessPayment(
                                          payment._id,
                                          commission.id,
                                          "paid",
                                          payment.amountPaid,
                                        )
                                      }
                                      disabled={!canWriteCommission}
                                    >
                                      Submit Payment
                                    </Button>
                                  </div>
                                )}

                                {/* Submit Rejection */}
                                {payment.status === "rejected" && (
                                  <Button
                                    size="sm"
                                    color="failure"
                                    className="w-full mt-3"
                                    onClick={() =>
                                      handleProcessPayment(
                                        payment._id,
                                        commission.id,
                                        "rejected",
                                      )
                                    }
                                    disabled={!canWriteCommission}
                                  >
                                    Confirm Rejection
                                  </Button>
                                )}
                              </div>
                            )}

                            {/* Display Paid Details */}
                            {/* {payment.paymentStatus === "paid" && (
                            <div className="text-sm pt-2 border-t border-dashed">
                              <p>
                                <span className="font-semibold">
                                  Actual Paid:{" "}
                                </span>
                                <strong>
                                  {payment.amountPaid
                                    ? payment.amountPaid.toLocaleString(
                                        "en-US",
                                        { style: "currency", currency: "NGN" }
                                      )
                                    : "-"}
                                </strong>
                              </p>
                              <p>
                                <span className="font-semibold">
                                  Date Paid:{" "}
                                </span>
                                {payment.datePaid
                                  ? new Date(
                                      payment.datePaid
                                    ).toLocaleDateString("en-GB", {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    })
                                  : "-"}
                              </p>
                            </div>
                          )} */}

                            {/* Account Info for all, if available */}
                            {payment.accountDetails && (
                              <p className="text-xs text-gray-500 pt-2 border-t border-dashed">
                                <strong>Account Details: </strong>
                                {payment.accountDetails.accountName} |{" "}
                                {payment.accountDetails.accountNumber} (
                                {payment.accountDetails.bankName})
                              </p>
                            )}
                          </div>
                        ))
                    )}
                  </div>
                </Card>
              </div>
            </div>

            <Modal
              show={showCustomerPaymentModal}
              size="md"
              popup={true}
              onClose={() => setShowCustomerPaymentModal(false)}
            >
              <Modal.Header>Add Customer Payment</Modal.Header>
              <Modal.Body>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="amountPaid">Amount Paid</Label>

                    <TextInput
                      id="amountPaid"
                      type="number"
                      step="0.01"
                      value={customerAmountPaid}
                      onChange={(e) => setCustomerAmountPaid(e.target.value)}
                      placeholder="Enter amount"
                      required
                    />
                  </div>

                  {/* <div>
                    <Label htmlFor="commissionEarnedSoFar">
                      Update Commission
                    </Label>

                    <TextInput
                      id="commissionEarnedSoFar"
                      type="number"
                            step="0.01"
                      value={commissionEarnedSoFar}
                      onChange={(e) => setCommissionEarnedSoFar(e.target.value)}
                      placeholder="Enter amount"
                      required
                    />
                  </div> */}

                  <div>
                    <Label htmlFor="datePaid">Date Paid (optional)</Label>
                    <TextInput
                      id="datePaid"
                      type="date"
                      value={customerDatePaid}
                      onChange={(e) => setCustomerDatePaid(e.target.value)}
                    />
                  </div>

                  <div>
                    <Label htmlFor="paymentProof">
                      Proof of Payment (optional)
                    </Label>
                    <input
                      id="paymentProof"
                      type="file"
                      accept="image/*,.pdf"
                      className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 p-2"
                      onChange={(e) =>
                        setPaymentProof(e.target.files?.[0] || null)
                      }
                    />
                  </div>
                  {customerPaymentError && (
                    <p className="text-red-600">{customerPaymentError}</p>
                  )}

                  <div className="flex justify-end gap-3">
                    <Button
                      color="gray"
                      onClick={() => {
                        setShowCustomerPaymentModal(false);
                        setCustomerAmountPaid("");
                        setCustomerDatePaid("");
                        setPaymentProof(null);
                        setCustomerPaymentError("");
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      color="success"
                      onClick={addCustomerPayment}
                      disabled={updating || !canWriteCommission}
                    >
                      {updating ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>
              </Modal.Body>
            </Modal>

            <Modal
              show={showMarketerPaymentModal}
              size="md"
              popup={true}
              onClose={() => setShowMarketerPaymentModal(false)}
            >
              <Modal.Header>Add Marketer Payment</Modal.Header>
              <Modal.Body>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="marketerAmountPaid">Amount Paid</Label>
                    <TextInput
                      id="marketerAmountPaid"
                      type="number"
                      step="0.01"
                      value={marketerAmountPaid}
                      onChange={(e) => setMarketerAmountPaid(e.target.value)}
                      placeholder="Enter amount"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="marketerDatePaid">
                      Date Paid (optional)
                    </Label>
                    <TextInput
                      id="marketerDatePaid"
                      type="date"
                      value={marketerDatePaid}
                      onChange={(e) => setMarketerDatePaid(e.target.value)}
                    />
                  </div>

                  {marketerPaymentError && (
                    <p className="text-red-600">{marketerPaymentError}</p>
                  )}

                  <div className="flex justify-end gap-3">
                    <Button
                      color="gray"
                      onClick={() => {
                        setShowMarketerPaymentModal(false);
                        setMarketerAmountPaid("");
                        setMarketerDatePaid("");
                        setMarketerPaymentError("");
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      color="success"
                      onClick={addMarketerPayment}
                      disabled={updating || !canWriteCommission}
                    >
                      {updating ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>
              </Modal.Body>
            </Modal>
            {/* Confirmation Modal */}
            <Modal
              show={openModal}
              size="md"
              onClose={() => setOpenModal(false)}
            >
              <Modal.Header>Confirm Action</Modal.Header>
              <Modal.Body>
                <div className="space-y-4">
                  <div className="text-lg">
                    Are you sure you want to{" "}
                    <span
                      className={`font-bold ${
                        selectedStatus === "approved"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {selectedStatus === "approved"
                        ? "APPROVE"
                        : "REJECT"}{" "}
                    </span>
                    this commission request?
                  </div>
                  {/* Show this ONLY if APPROVED */}
                  {selectedStatus === "approved" && (
                    <div>
                      <Label
                        htmlFor="totalCommissionExpected"
                        value="Total Commission Expected (NGN)"
                      />

                      <TextInput
                        id="totalCommissionExpected"
                        type="number"
                        step="0.01"
                        placeholder="Enter expected commission"
                        value={totalCommissionExpected}
                        onChange={(e) =>
                          setTotalCommissionExpected(e.target.value)
                        }
                        required
                      />

                      {errorMessage && (
                        <p className="text-red-600 text-sm mt-1">
                          {errorMessage}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </Modal.Body>

              <Modal.Footer>
                <Button
                  color={selectedStatus === "approved" ? "success" : "failure"}
                  onClick={confirmAction}
                  disabled={!canWriteCommission}
                >
                  Yes, {selectedStatus === "approved" ? "Approve" : "Reject"}
                </Button>
                <Button color="gray" onClick={() => setOpenModal(false)}>
                  Cancel
                </Button>
              </Modal.Footer>
            </Modal>
            {toastOpen && (
              <CustomToast
                message={toastMessage}
                type={toastType}
                duration={3000}
                onClose={() => setToastOpen(false)}
              />
            )}
          </div>
        </div>
    </DashboardLayout>
  );
}

const InfoBox = ({ title, children }) => (
  <div>
    <p className="text-sm font-medium text-gray-500">{title}</p>
    <p className="text-lg font-semibold text-gray-900 mt-0.5">{children}</p>
  </div>
);

const FinancialStat = ({ title, value, color }) => (
  <div className="flex flex-col items-start bg-gray-50 p-3 rounded-lg border-l-4 border-blue-200">
    <p className="text-sm font-medium text-gray-500">{title}</p>
    <p className={`text-xl font-bold mt-1 ${color}`}>{value}</p>
  </div>
);
